import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ONBOARDING } from "@/constants/testIds";
import { ThemeToggle } from "@/components/ThemeToggle";
import { toast } from "sonner";
import {
  Sparkles, ArrowRight, ArrowLeft, BookOpen, Rocket, Heart, Briefcase,
  MessageCircle, Zap, Target, Code2, Globe, Sigma, Palette, Wand2,
} from "lucide-react";

const PERSONALITIES = [
  { key: "Professor", desc: "Deep, thorough, academic tone.", icon: BookOpen },
  { key: "Friendly Coach", desc: "Warm, encouraging, casual.", icon: Heart },
  { key: "Motivational", desc: "Energetic, celebratory, hype.", icon: Rocket },
  { key: "Professional", desc: "Concise, business-like, precise.", icon: Briefcase },
  { key: "Supportive", desc: "Patient, empathetic, safe.", icon: MessageCircle },
  { key: "Socratic", desc: "Asks probing questions to teach.", icon: Wand2 },
  { key: "Straight to the Point", desc: "No fluff, direct answers.", icon: Zap },
];

const PACES = [
  { key: "Slow", desc: "Small steps, deep understanding." },
  { key: "Balanced", desc: "The sweet spot for most people." },
  { key: "Fast", desc: "Move quickly, high challenge." },
];

const STYLES = [
  { key: "Examples", icon: Target },
  { key: "Analogies", icon: Sparkles },
  { key: "Visual", icon: Palette },
  { key: "Mathematical", icon: Sigma },
  { key: "Code-first", icon: Code2 },
  { key: "Real-world", icon: Globe },
];

const SUGGESTED_NAMES = ["Lane", "Aria", "Nova", "Kai", "Sage", "Milo", "Iris"];

export default function OnboardingPage() {
  const { user, refreshMe } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [name, setName] = useState("Lane");
  const [personality, setPersonality] = useState("Friendly Coach");
  const [pace, setPace] = useState("Balanced");
  const [styles, setStyles] = useState(["Examples", "Analogies"]);
  const [goals, setGoals] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user && user.onboarded) navigate("/app/dashboard", { replace: true });
  }, [user, navigate]);

  const toggleStyle = (s) =>
    setStyles((v) => (v.includes(s) ? v.filter((x) => x !== s) : [...v, s]));

  const save = async () => {
    if (styles.length === 0) { toast.error("Pick at least one style"); return; }
    setSaving(true);
    try {
      await api.post("/onboarding/mentor", {
        mentor_name: name.trim() || "Lane",
        personality, pace, explanation_styles: styles,
        learning_goals: goals.trim() || null,
      });
      await refreshMe();
      toast.success(`${name} is ready to teach you.`);
      navigate("/app/dashboard", { replace: true });
    } catch {
      toast.error("Could not save. Try again.");
    } finally { setSaving(false); }
  };

  const steps = [
    // Step 0: Name
    (
      <div className="space-y-6">
        <div>
          <div className="label-mono text-primary">// step 1 of 4</div>
          <h2 className="mt-2 font-display text-3xl font-black tracking-tighter">Name your mentor.</h2>
          <p className="mt-2 text-sm text-muted-foreground">Give them a name — this is who you'll be learning with every day.</p>
        </div>
        <Input
          data-testid={ONBOARDING.nameInput}
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Lane"
          className="h-12 rounded-lg text-lg"
          maxLength={40}
        />
        <div className="flex flex-wrap gap-2">
          {SUGGESTED_NAMES.map((n) => (
            <button
              key={n}
              onClick={() => setName(n)}
              className={`rounded-full border px-4 py-1.5 font-mono text-xs uppercase tracking-widest transition ${name === n ? "border-primary bg-primary/10 text-primary" : "border-border hover:border-primary/40"}`}
            >
              {n}
            </button>
          ))}
        </div>
      </div>
    ),
    // Step 1: Personality
    (
      <div className="space-y-6">
        <div>
          <div className="label-mono text-primary">// step 2 of 4</div>
          <h2 className="mt-2 font-display text-3xl font-black tracking-tighter">Choose {name || "your mentor"}'s personality.</h2>
          <p className="mt-2 text-sm text-muted-foreground">How should they talk to you?</p>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          {PERSONALITIES.map((p) => (
            <button
              key={p.key}
              data-testid={ONBOARDING.personality(p.key)}
              onClick={() => setPersonality(p.key)}
              className={`flex items-start gap-3 rounded-xl border p-4 text-left transition ${personality === p.key ? "border-primary bg-primary/10" : "border-border hover:border-primary/40"}`}
            >
              <p.icon className={`mt-0.5 h-5 w-5 flex-shrink-0 ${personality === p.key ? "text-primary" : "text-muted-foreground"}`} />
              <div>
                <div className="font-display font-bold">{p.key}</div>
                <div className="mt-1 text-xs text-muted-foreground">{p.desc}</div>
              </div>
            </button>
          ))}
        </div>
      </div>
    ),
    // Step 2: Pace
    (
      <div className="space-y-6">
        <div>
          <div className="label-mono text-primary">// step 3 of 4</div>
          <h2 className="mt-2 font-display text-3xl font-black tracking-tighter">Pick your pace.</h2>
          <p className="mt-2 text-sm text-muted-foreground">Don't worry — you can change this anytime.</p>
        </div>
        <div className="grid gap-3 sm:grid-cols-3">
          {PACES.map((p) => (
            <button
              key={p.key}
              data-testid={ONBOARDING.pace(p.key)}
              onClick={() => setPace(p.key)}
              className={`rounded-xl border p-4 text-left transition ${pace === p.key ? "border-primary bg-primary/10" : "border-border hover:border-primary/40"}`}
            >
              <div className="font-display text-lg font-bold">{p.key}</div>
              <div className="mt-1 text-xs text-muted-foreground">{p.desc}</div>
            </button>
          ))}
        </div>
      </div>
    ),
    // Step 3: Styles + goals
    (
      <div className="space-y-6">
        <div>
          <div className="label-mono text-primary">// step 4 of 4</div>
          <h2 className="mt-2 font-display text-3xl font-black tracking-tighter">How do you learn best?</h2>
          <p className="mt-2 text-sm text-muted-foreground">Pick one or more explanation styles.</p>
        </div>
        <div className="grid gap-3 sm:grid-cols-3">
          {STYLES.map((s) => {
            const selected = styles.includes(s.key);
            return (
              <button
                key={s.key}
                data-testid={ONBOARDING.style(s.key)}
                onClick={() => toggleStyle(s.key)}
                className={`flex items-center gap-2 rounded-xl border p-4 transition ${selected ? "border-primary bg-primary/10" : "border-border hover:border-primary/40"}`}
              >
                <s.icon className={`h-4 w-4 ${selected ? "text-primary" : "text-muted-foreground"}`} />
                <div className="font-display font-bold">{s.key}</div>
              </button>
            );
          })}
        </div>
        <div>
          <div className="label-mono">Your goal (optional)</div>
          <Textarea
            data-testid={ONBOARDING.goalsInput}
            value={goals}
            onChange={(e) => setGoals(e.target.value)}
            placeholder="e.g. Land a job as a data engineer within 6 months."
            className="mt-2 min-h-[80px] rounded-lg"
          />
        </div>
      </div>
    ),
  ];

  return (
    <div data-testid={ONBOARDING.root} className="min-h-screen bg-background text-foreground">
      <header className="border-b border-border">
        <div className="mx-auto flex max-w-4xl items-center justify-between px-4 py-4">
          <div className="flex items-center gap-2">
            <img src="/lanex-mark.png" alt="LaneX Academy" className="h-9 w-auto object-contain bg-white/95 rounded-lg p-1 shadow-md ring-1 ring-primary/30 drop-shadow-[0_0_12px_rgba(0,238,199,0.45)]" />
            <span className="font-display font-black">LaneX Academy</span>
          </div>
          <ThemeToggle />
        </div>
      </header>

      <main className="mx-auto max-w-2xl px-4 py-12 md:py-16">
        {/* Progress dots */}
        <div className="mb-10 flex gap-2">
          {steps.map((_, i) => (
            <div key={i} className={`h-1 flex-1 rounded-full transition ${i <= step ? "bg-primary" : "bg-secondary"}`} />
          ))}
        </div>

        <div className="rounded-2xl border border-border bg-card p-8 md:p-10">
          {steps[step]}
        </div>

        <div className="mt-6 flex items-center justify-between">
          <Button
            data-testid={ONBOARDING.backBtn}
            variant="outline"
            className="rounded-full font-mono text-xs uppercase tracking-widest"
            onClick={() => setStep((s) => Math.max(0, s - 1))}
            disabled={step === 0}
          >
            <ArrowLeft className="mr-2 h-4 w-4" /> Back
          </Button>
          {step < steps.length - 1 ? (
            <Button
              data-testid={ONBOARDING.nextBtn}
              className="rounded-full btn-neon font-mono text-xs uppercase tracking-widest"
              onClick={() => setStep((s) => s + 1)}
              disabled={step === 0 && !name.trim()}
            >
              Next <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          ) : (
            <Button
              data-testid={ONBOARDING.submitBtn}
              className="rounded-full btn-neon font-mono text-xs uppercase tracking-widest"
              onClick={save}
              disabled={saving || styles.length === 0}
            >
              {saving ? "Saving…" : `Meet ${name}`} <Sparkles className="ml-2 h-4 w-4" />
            </Button>
          )}
        </div>
      </main>
    </div>
  );
}
